import React, { useState } from 'react';
import { Send, Mail, Phone, MapPin, User, Briefcase, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

export const ContactSection = ({ t }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    profession: '',
    type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success('¡Consulta enviada con éxito! Nos pondremos en contacto pronto.');
        setFormData({ name: '', email: '', phone: '', country: '', profession: '', type: '', message: '' });
      } else {
        toast.error('Error al enviar. Intenta de nuevo.');
      }
    } catch {
      toast.error('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: t.footer.email,
      link: `mailto:${t.footer.email}`
    },
    {
      icon: Phone,
      label: 'WhatsApp',
      value: t.footer.phone,
      link: `https://wa.me/13059795251`
    },
    {
      icon: MapPin,
      label: 'Dirección',
      value: t.footer.address,
      link: null
    }
  ];

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.contact.title}
          </h2>
          <p className="text-xl text-slate-700 font-semibold mb-4">
            {t.contact.subtitle}
          </p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t.contact.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-slate-700">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-slate-800 transition-colors duration-300">
                        <Icon className="w-6 h-6 text-slate-700 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-500 mb-1">{info.label}</p>
                        {info.link ? (
                          <a href={info.link} className="text-gray-900 hover:text-slate-700 transition-colors font-medium">
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-gray-900 font-medium">{info.value}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Additional Info Card */}
            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-3">Horario de Atención</h3>
                <div className="space-y-2 text-white/90">
                  <p>Lunes - Viernes: 9:00 - 18:00</p>
                  <p>Sábado: 10:00 - 14:00</p>
                  <p>Domingo: Cerrado</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-slate-200 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-base font-semibold flex items-center text-slate-700">
                      <User className="w-4 h-4 mr-2 text-slate-600" />
                      {t.contact.form.name}
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                      className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800"
                    />
                  </div>

                  {/* Email and Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-base font-semibold flex items-center text-slate-700">
                        <Mail className="w-4 h-4 mr-2 text-slate-600" />
                        {t.contact.form.email}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-base font-semibold flex items-center text-slate-700">
                        <Phone className="w-4 h-4 mr-2 text-slate-600" />
                        {t.contact.form.phone}
                      </Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                        className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800"
                      />
                    </div>
                  </div>

                  {/* Country and Profession */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-base font-semibold flex items-center text-slate-700">
                        <MapPin className="w-4 h-4 mr-2 text-slate-600" />
                        {t.contact.form.country}
                      </Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                        required
                        className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profession" className="text-base font-semibold flex items-center text-slate-700">
                        <Briefcase className="w-4 h-4 mr-2 text-slate-600" />
                        {t.contact.form.profession}
                      </Label>
                      <Input
                        id="profession"
                        value={formData.profession}
                        onChange={(e) => handleChange('profession', e.target.value)}
                        required
                        className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800"
                      />
                    </div>
                  </div>

                  {/* Inquiry Type */}
                  <div className="space-y-2">
                    <Label htmlFor="type" className="text-base font-semibold flex items-center text-slate-700">
                      <MessageSquare className="w-4 h-4 mr-2 text-slate-600" />
                      {t.contact.form.type}
                    </Label>
                    <Select value={formData.type} onValueChange={(value) => handleChange('type', value)} required>
                      <SelectTrigger className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800">
                        <SelectValue placeholder="Selecciona el tipo de consulta" />
                      </SelectTrigger>
                      <SelectContent>
                        {t.contact.form.typeOptions.map((option, index) => (
                          <SelectItem key={index} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-base font-semibold text-slate-700">
                      {t.contact.form.message}
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      required
                      rows={5}
                      className="border-slate-300 focus:border-slate-800 focus:ring-slate-800 resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="w-full bg-slate-800 hover:bg-slate-900 text-white h-14 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 group"
                  >
                    <Send className="mr-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    {loading ? '...' : t.contact.form.submit}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};